import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Trash2, Eye, Mail, Phone, MapPin, Calendar, Users, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  new: { label: "Nouveau", color: "bg-gold text-white" },
  contacted: { label: "Contacté", color: "bg-blue-500 text-white" },
  member: { label: "Membre", color: "bg-emerald-600 text-white" },
};

export default function AdminRegistrations() {
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("new");

  const load = () => {
    const params = filter === "all" ? {} : { status: filter };
    api.get("/registrations", { params }).then((r) => setItems(r.data)).catch(() => {});
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const openDetail = (r) => {
    setSelected(r);
    setNotes(r.notes || "");
    setStatus(r.status || "new");
  };

  const saveDetail = async () => {
    try {
      await api.put(`/registrations/${selected.id}`, { status, notes });
      toast.success("Mise à jour effectuée");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Supprimer l'inscription de ${r.first_name} ${r.last_name} ?`)) return;
    await api.delete(`/registrations/${r.id}`);
    toast.success("Supprimé");
    load();
  };

  return (
    <div data-testid="admin-registrations">
      <div className="mb-8">
        <div className="text-gold-dark text-xs tracking-[0.3em] uppercase">Communauté</div>
        <h1 className="font-serif text-3xl sm:text-4xl text-deepblue-900 mt-1">Inscriptions des fidèles</h1>
        <p className="text-slate-500 mt-2">Gérez les nouvelles inscriptions et suivez l'accueil des membres.</p>
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList data-testid="registrations-tabs">
          <TabsTrigger value="all">Toutes</TabsTrigger>
          <TabsTrigger value="new">Nouveaux</TabsTrigger>
          <TabsTrigger value="contacted">Contactés</TabsTrigger>
          <TabsTrigger value="member">Membres</TabsTrigger>
        </TabsList>

        <TabsContent value={filter} className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.length === 0 && (
            <div className="col-span-full text-center py-16 text-slate-400 bg-white rounded-2xl border border-slate-200">
              Aucune inscription
            </div>
          )}
          {items.map((r) => {
            const s = STATUS[r.status] || STATUS.new;
            return (
              <div key={r.id} className="rounded-2xl bg-white border border-slate-200 p-6 card-lift" data-testid={`registration-${r.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-serif text-lg text-deepblue-900">{r.first_name} {r.last_name}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{new Date(r.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full font-semibold ${s.color}`}>
                    {s.label}
                  </span>
                </div>

                <div className="space-y-1.5 text-sm text-slate-600">
                  <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" /><a href={`mailto:${r.email}`} className="hover:text-gold-dark truncate">{r.email}</a></div>
                  <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" /><a href={`tel:${r.phone}`} className="hover:text-gold-dark">{r.phone}</a></div>
                  {r.city && <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" />{r.city}</div>}
                </div>

                {r.interested_groups?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {r.interested_groups.slice(0, 3).map((g) => (
                      <span key={g} className="text-[10px] px-2 py-0.5 rounded bg-blue-50 text-deepblue-700 border border-blue-200">{g}</span>
                    ))}
                    {r.interested_groups.length > 3 && (
                      <span className="text-[10px] text-slate-400">+{r.interested_groups.length - 3}</span>
                    )}
                  </div>
                )}

                {r.is_new_convert && (
                  <div className="mt-3 inline-flex items-center gap-1 text-xs text-gold-dark font-semibold">
                    ✝️ Nouveau converti
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-slate-100 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openDetail(r)} data-testid={`view-registration-${r.id}`}>
                    <Eye className="w-4 h-4 mr-1" /> Détails
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(r)}>
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
              </div>
            );
          })}
        </TabsContent>
      </Tabs>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selected?.first_name} {selected?.last_name}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <Info icon={Mail} label="Email" value={<a href={`mailto:${selected.email}`} className="text-gold-dark hover:underline">{selected.email}</a>} />
                <Info icon={Phone} label="Téléphone" value={<a href={`tel:${selected.phone}`} className="text-gold-dark hover:underline">{selected.phone}</a>} />
                {selected.birth_date && <Info icon={Calendar} label="Date de naissance" value={new Date(selected.birth_date).toLocaleDateString("fr-FR")} />}
                {selected.gender && <Info icon={Users} label="Sexe" value={selected.gender === "M" ? "Masculin" : selected.gender === "F" ? "Féminin" : "Autre"} />}
                {selected.city && <Info icon={MapPin} label="Ville" value={selected.city} />}
                {selected.marital_status && <Info icon={Users} label="Statut" value={selected.marital_status} />}
              </div>

              {selected.address && (
                <div className="p-3 rounded-lg bg-slate-50 text-sm">
                  <div className="text-xs uppercase text-slate-400">Adresse</div>
                  <div>{selected.address}</div>
                </div>
              )}

              {selected.how_did_you_hear && (
                <div className="p-3 rounded-lg bg-slate-50 text-sm">
                  <div className="text-xs uppercase text-slate-400">Comment nous a connu</div>
                  <div>{selected.how_did_you_hear}</div>
                </div>
              )}

              {selected.interested_groups?.length > 0 && (
                <div>
                  <div className="text-xs uppercase text-slate-400 mb-2">Groupes d'intérêt</div>
                  <div className="flex flex-wrap gap-1">
                    {selected.interested_groups.map((g) => (
                      <span key={g} className="text-xs px-2.5 py-1 rounded bg-blue-50 text-deepblue-700 border border-blue-200">{g}</span>
                    ))}
                  </div>
                </div>
              )}

              {selected.is_new_convert && (
                <div className="p-3 rounded-lg bg-gold/10 border border-gold/30 text-sm text-gold-dark font-semibold">
                  ✝️ Nouveau converti — accompagnement recommandé
                </div>
              )}

              {selected.prayer_request && (
                <div className="p-4 rounded-lg bg-pink-50 border border-pink-200">
                  <div className="text-xs uppercase text-pink-700 font-semibold mb-1">Demande de prière</div>
                  <div className="text-sm text-slate-700 whitespace-pre-wrap">{selected.prayer_request}</div>
                </div>
              )}

              <div className="pt-4 border-t space-y-3">
                <div>
                  <label className="text-xs uppercase text-slate-500 tracking-widest">Statut</label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">Nouveau</SelectItem>
                      <SelectItem value="contacted">Contacté</SelectItem>
                      <SelectItem value="member">Membre officiel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs uppercase text-slate-500 tracking-widest">Notes internes</label>
                  <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                    placeholder="Notes de suivi, contact effectué le…" className="mt-1" data-testid="reg-notes" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>Fermer</Button>
            <Button onClick={saveDetail} className="bg-gold hover:bg-gold-hover text-white" data-testid="save-registration-btn">
              <CheckCircle className="w-4 h-4 mr-2" /> Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="p-3 rounded-lg bg-slate-50">
      <div className="flex items-center gap-1.5 text-xs uppercase text-slate-400">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="mt-1 font-medium text-deepblue-900">{value}</div>
    </div>
  );
}
